/**
 * Pokédex modal — Future Cartridge revamp (PR 4 of TODOs #24).
 *
 * Two tabs: THIS RUN + CAREER.
 *
 * THIS RUN renders three sub-sections from runState.pokedex:
 *   DRAFTED — snapshots written on draft pick. Tappable rows open
 *             PokemonFullSheet for that historical Pokémon. Status dot
 *             is --ok-green (alive) or --err-red (fainted), JOIN'd
 *             against careerStore at render time.
 *   SKIPPED — the OTHER offers each modal didn't pick. Tappable rows
 *             open the full sheet of the road not taken.
 *   SEEN WILD — per-species count + level band from grass encounters.
 *               Read-only (no offer data behind the species).
 *
 * CAREER renders RECENT DEATHS (last 10) + BY POKÉMON aggregate from
 * careerStore. Death rows are tappable; aggregate rows are static.
 *
 * Empty states use a centered headline + body. Default tab resets to
 * THIS RUN on every modal open per the eng review.
 *
 * Lifecycle: caller.scene.sleep() + scene.launch('PokedexScene');
 * close() stops + wakes WorldScene. Backdrop tap, ESC, B all close.
 */
import Phaser from 'phaser';
import {
  runState,
  type Gender,
  type IvSet,
  type PartyMemberSnapshot,
  type SkippedEntry,
} from '../state/runState';
import { careerStore, type CareerEntry } from '../state/careerStore';
import { audio } from '../systems/audio';
import { PokemonFullSheet } from './PokemonFullSheet';
import { snapshotToOffer } from '../state/offerHelpers';
import type { DraftOffer } from '../state/draftRoll';
import type { SnapshotHandle } from '../render/snapshotBackdrop';
import type { FullSheetData } from '../data/dossierProse';
import {
  type DossierContext,
  type PokedexTab,
} from '../ui/dossierTabStripLogic';
import { DossierTabStrip } from '../ui/dossierTabStrip';
import { gameAbilityDesc } from '../data/abilityDescOverrides';

function sumIvs(ivs: IvSet): number {
  return ivs.hp + ivs.atk + ivs.def + ivs.spa + ivs.spd + ivs.spe;
}

const POKEDEX_TAB_LABELS: Record<PokedexTab, string> = {
  'this-run': 'This Run',
  career: 'Career',
};

const POKEDEX_TAB_ABBREV: Record<PokedexTab, string> = {
  'this-run': 'RUN',
  career: 'CAR',
};

const POKEDEX_VISIBLE_TABS: readonly PokedexTab[] = ['this-run', 'career'];

type OverlayMode = 'live' | 'frozen';

export interface PokedexSceneData {
  snapshotHandle?: SnapshotHandle | null;
}

function titleCase(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

export class PokedexScene extends Phaser.Scene {
  private modalRoot: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private tabStrip: DossierTabStrip<PokedexTab> | null = null;
  private currentTab: PokedexTab = 'this-run';
  private fullSheet: PokemonFullSheet | null = null;
  private fullSheetOpen = false;
  private overlayMode: OverlayMode = 'live';
  private snapshotHandle: SnapshotHandle | null = null;

  constructor() {
    super('PokedexScene');
  }

  init(data: PokedexSceneData = {}): void {
    this.currentTab = 'this-run';
    this.fullSheet = null;
    this.fullSheetOpen = false;
    this.snapshotHandle = data.snapshotHandle ?? null;
    this.overlayMode = this.snapshotHandle ? 'frozen' : 'live';
  }

  create(): void {
    // Glass-Over-World PR 3b: frozen path claims the snapshot;
    // live path stays transparent so WorldScene shows through.
    if (this.snapshotHandle) {
      this.snapshotHandle.transferOwnership(this);
    } else {
      this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    }
    this.add
      .rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0)
      .setOrigin(0)
      .setInteractive()
      .on('pointerdown', () => undefined);
    this.mountDOM();

    this.input.keyboard?.on('keydown-ESC', () => this.close());
    this.input.keyboard?.on('keydown-B', () => this.close());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private mountDOM(): void {
    const gameEl = document.getElementById('game');
    if (!gameEl) return;

    const root = document.createElement('div');
    root.className = 'pokedex-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Pokédex');
    root.addEventListener('click', (e) => {
      if (e.target === root) this.close();
    });

    const card = document.createElement('div');
    card.className = 'pokedex-card';

    const header = document.createElement('header');
    header.className = 'pokedex-card__header';
    const title = document.createElement('h1');
    title.className = 'pokedex-card__title';
    title.textContent = 'Pokédex';
    header.appendChild(title);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'pokedex-card__close';
    close.setAttribute('aria-label', 'Close Pokédex');
    close.textContent = '×';
    close.addEventListener('click', () => this.close());
    header.appendChild(close);
    card.appendChild(header);

    // Tabs strip — migrated from inline .pokedex-tab buttons to the shared
    // dossierTabStrip primitive (PR 4a STRUCTURAL). 2-tab variant; same
    // user-visible behavior.
    this.tabStrip = new DossierTabStrip<PokedexTab>({
      visibleTabs: POKEDEX_VISIBLE_TABS,
      initialTab: this.currentTab,
      labelFor: (t) => POKEDEX_TAB_LABELS[t],
      labelAbbrevFor: (t) => POKEDEX_TAB_ABBREV[t],
      onTabChange: (t) => this.switchToTab(t),
    });
    card.appendChild(this.tabStrip.getElement());

    // Scrollable body
    const body = document.createElement('div');
    body.className = 'pokedex-card__body';
    card.appendChild(body);
    this.body = body;

    this.renderTab();

    root.appendChild(card);
    gameEl.appendChild(root);
    this.modalRoot = root;
  }

  /** Tab-change callback wired through dossierTabStrip's onTabChange.
   *  Plays the audio cue + swaps the rendered tab body. Strip handles its
   *  own aria-selected + tabIndex updates internally. */
  private switchToTab(tab: PokedexTab): void {
    if (this.currentTab === tab) return;
    audio.playSfx('select');
    this.currentTab = tab;
    this.renderTab();
  }

  private renderTab(): void {
    if (!this.body) return;
    this.body.replaceChildren();
    if (this.currentTab === 'this-run') {
      this.renderThisRunTab();
    } else {
      this.renderCareerTab();
    }
    this.body.scrollTop = 0;
  }

  // ─── THIS RUN ────────────────────────────────────────────────────────

  private renderThisRunTab(): void {
    if (!this.body) return;
    const pdx = runState.getPokedex();
    const empty =
      pdx.drafted.length === 0 &&
      pdx.skipped.length === 0 &&
      Object.keys(pdx.seenWild).length === 0;
    if (empty) {
      this.body.appendChild(
        this.buildEmptyState('No party yet.', "Visit PROF. OAK's lab to begin."),
      );
      return;
    }

    if (pdx.drafted.length > 0) {
      this.body.appendChild(this.buildSectionLabel('Drafted'));
      for (const snap of pdx.drafted) {
        this.body.appendChild(this.buildDraftedRow(snap));
      }
    }

    if (pdx.skipped.length > 0) {
      this.body.appendChild(this.buildSectionLabel('Skipped'));
      for (const skipped of pdx.skipped) {
        this.body.appendChild(this.buildSkippedRow(skipped));
      }
    }

    const seenSpecies = Object.keys(pdx.seenWild);
    if (seenSpecies.length > 0) {
      this.body.appendChild(this.buildSectionLabel('Seen Wild'));
      for (const species of seenSpecies) {
        const entry = pdx.seenWild[species]!;
        const displayName = titleCase(species);
        const meta = `× ${entry.count} · Lv ${entry.minLevel}-${entry.maxLevel}`;
        this.body.appendChild(this.buildStaticRow(displayName, meta));
      }
    }
  }

  private buildDraftedRow(snap: PartyMemberSnapshot): HTMLButtonElement {
    const career = careerStore.findById(snap.id);
    const isDead = career?.diedAt != null;
    return this.buildSlimRow({
      onClick: () => this.openDraftedDossier(snap),
      dotState: isDead ? 'dead' : 'alive',
      species: snap.displayName,
      gender: snap.gender,
      level: snap.level,
      ivTotal: sumIvs(snap.ivs),
      dexId: snap.dexId,
      ariaLabel: `${snap.displayName}, level ${snap.level}${
        isDead ? ', fainted' : ', alive'
      }`,
    });
  }

  private buildSkippedRow(skipped: SkippedEntry): HTMLButtonElement {
    return this.buildSlimRow({
      onClick: () =>
        this.openFullSheet(
          { kind: 'snapshot', offer: skipped.offer },
          'snapshot-context',
        ),
      dotState: 'skipped',
      species: skipped.offer.displayName,
      gender: skipped.offer.gender,
      level: skipped.offer.level,
      ivTotal: sumIvs(skipped.offer.ivs),
      dexId: skipped.offer.dexId,
      ariaLabel: `Skipped ${skipped.offer.displayName}, level ${skipped.offer.level}`,
    });
  }

  // ─── CAREER ──────────────────────────────────────────────────────────

  private renderCareerTab(): void {
    if (!this.body) return;
    const totals = careerStore.getTotals();
    const entries = careerStore.getEntries();
    const aliveThisRun = runState.getParty().length;

    if (totals.drafts === 0) {
      this.body.appendChild(
        this.buildEmptyState('No runs yet.', 'Your graveyard fills as you play.'),
      );
      return;
    }

    const headerLine = document.createElement('div');
    headerLine.className = 'pokedex-card__header-line';
    headerLine.textContent = `${totals.drafts} drafts · ${totals.deaths} deaths · ${aliveThisRun} still alive (this run)`;
    this.body.appendChild(headerLine);

    const deaths = entries.filter((e) => e.diedAt !== null).slice(0, 10);
    if (deaths.length > 0) {
      this.body.appendChild(this.buildSectionLabel('Recent Deaths'));
      for (const e of deaths) {
        this.body.appendChild(this.buildDeathRow(e));
      }
    }

    const agg = careerStore.aggregateBySpecies();
    if (agg.size > 0) {
      this.body.appendChild(this.buildSectionLabel('By Pokémon'));
      const sorted = [...agg.entries()].sort((a, b) => b[1].drafted - a[1].drafted);
      for (const [species, stats] of sorted) {
        const displayName = titleCase(species);
        const meta = `drafted ${stats.drafted} · ☠ ${stats.deaths}`;
        this.body.appendChild(this.buildStaticRow(displayName, meta));
      }
    }
  }

  private buildDeathRow(e: CareerEntry): HTMLButtonElement {
    return this.buildSlimRow({
      onClick: () => this.openCareerDossier(e),
      dotState: 'dead',
      species: e.displayName,
      gender: e.gender ?? 'genderless',
      level: e.levelAtDeath ?? e.level,
      ivTotal: sumIvs(e.ivs),
      dexId: e.dexId,
      ariaLabel: `${e.displayName}, level ${e.levelAtDeath ?? e.level}, fainted to ${e.diedTo ?? 'unknown'}`,
    });
  }

  // ─── Row builders ────────────────────────────────────────────────────

  private buildSectionLabel(text: string): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'pokedex-card__section-label';
    el.textContent = text;
    return el;
  }

  /**
   * Slim catalog row per Premise 3 + DR-13: thumb (24×24) + species +
   * gender glyph + level + status dot + IV total. 44 px touch target.
   *
   * Used by DRAFTED (alive/dead via JOIN), SKIPPED (○hollow dot), and
   * CAREER RECENT DEATHS (red dot). The BY POKÉMON aggregate uses
   * `buildStaticRow` — no individual identity, no IV total.
   */
  private buildSlimRow(opts: {
    onClick: () => void;
    dotState: 'alive' | 'dead' | 'skipped' | 'none';
    species: string;
    gender: Gender;
    level: number;
    ivTotal: number;
    dexId: number;
    ariaLabel: string;
  }): HTMLButtonElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'pokedex-row pokedex-row--slim';
    row.setAttribute('aria-label', opts.ariaLabel);

    if (opts.dexId > 0) {
      const thumb = document.createElement('img');
      thumb.className = 'pokedex-row__thumb';
      thumb.src = `/assets/pokemon/orb/${opts.dexId}.gif`;
      thumb.alt = '';
      thumb.width = 24;
      thumb.height = 24;
      row.appendChild(thumb);
    }

    const nameSpan = document.createElement('span');
    nameSpan.className = 'pokedex-row__species';
    nameSpan.textContent = opts.species;
    row.appendChild(nameSpan);

    row.appendChild(this.buildGenderGlyph(opts.gender));

    const levelChip = document.createElement('span');
    levelChip.className = 'pokedex-row__level';
    levelChip.textContent = `Lv ${opts.level}`;
    row.appendChild(levelChip);

    const dot = document.createElement('span');
    dot.className = 'pokedex-row__dot';
    dot.dataset.state = opts.dotState;
    row.appendChild(dot);

    const iv = document.createElement('span');
    iv.className = 'pokedex-row__iv-total';
    iv.textContent = String(opts.ivTotal);
    row.appendChild(iv);

    row.addEventListener('click', opts.onClick);
    return row;
  }

  private buildGenderGlyph(gender: Gender): HTMLElement {
    const span = document.createElement('span');
    span.className = 'pokedex-row__gender';
    if (gender === 'male') {
      span.textContent = '♂';
      span.style.color = 'rgba(75, 141, 255, 0.70)';
    } else if (gender === 'female') {
      span.textContent = '♀';
      span.style.color = 'rgba(255, 147, 200, 0.70)';
    } else {
      span.textContent = '—';
      span.style.color = 'var(--text-tertiary)';
    }
    return span;
  }

  /**
   * Aggregate row for BY POKÉMON — species + drafted/deaths counts. No
   * individual identity, so no thumb / gender / IV total. Stays in the
   * pokedex-row container class for spacing consistency.
   */
  private buildStaticRow(species: string, meta: string): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'pokedex-row pokedex-row--static';

    const nameSpan = document.createElement('span');
    nameSpan.className = 'pokedex-row__species';
    nameSpan.textContent = species;
    row.appendChild(nameSpan);

    const metaSpan = document.createElement('span');
    metaSpan.className = 'pokedex-row__species-meta';
    metaSpan.textContent = meta;
    row.appendChild(metaSpan);

    return row;
  }

  private buildEmptyState(headline: string, body: string): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'pokedex-empty';
    const h = document.createElement('p');
    h.className = 'pokedex-empty__headline';
    h.textContent = headline;
    el.appendChild(h);
    const b = document.createElement('p');
    b.className = 'pokedex-empty__body';
    b.textContent = body;
    el.appendChild(b);
    return el;
  }

  // ─── PokemonFullSheet integration ────────────────────────────────────

  /**
   * Open the dossier for a THIS RUN > DRAFTED row. EN-7 mapping:
   *
   *   alive member found    → kind 'live', live-context
   *   careerStore says dead → kind 'career-snapshot', live-context
   *                          (death visible mid-run; STATS-first per EN-7 row 3)
   *   neither (defensive)   → kind 'snapshot', live-context
   */
  private openDraftedDossier(snap: PartyMemberSnapshot): void {
    const offer = snapshotToOffer(snap);
    const member = runState.getParty().find((m) => m.id === snap.id);
    const career = careerStore.findById(snap.id);
    if (
      career?.diedAt != null
      && career.diedTo
      && career.diedZone
      && career.levelAtDeath != null
    ) {
      this.openFullSheet(
        { kind: 'career-snapshot', offer, entry: career },
        'live-context',
      );
    } else if (member) {
      this.openFullSheet({ kind: 'live', offer, member }, 'live-context');
    } else {
      // Defensive: snapshot exists but no live member + no career death.
      // Should not occur since pokedex.drafted clears on Reset Run and any
      // mid-run death writes a careerStore entry. Fall back to snapshot mode.
      this.openFullSheet({ kind: 'snapshot', offer }, 'live-context');
    }
  }

  /**
   * Open the dossier for a CAREER > RECENT DEATHS row. EN-7 row 3 vs 4:
   * if the entry's id appears in THIS RUN's pokedex.drafted, it's a
   * fellow current-run casualty → live-context (STATS-first); otherwise
   * a prior-run memorial → snapshot-context (ABOUT-first).
   */
  private openCareerDossier(e: CareerEntry): void {
    const offer = careerEntryToOffer(e);
    const inThisRun = runState
      .getPokedex()
      .drafted.some((d) => d.id === e.id);
    const context: DossierContext = inThisRun
      ? 'live-context'
      : 'snapshot-context';
    this.openFullSheet({ kind: 'career-snapshot', offer, entry: e }, context);
  }

  private openFullSheet(data: FullSheetData, context: DossierContext): void {
    if (this.fullSheetOpen) return;
    this.fullSheetOpen = true;
    this.fullSheet = new PokemonFullSheet(this, data, {
      context,
      cta: 'back-only',
      onBack: () => this.closeFullSheet(),
    });
  }

  private closeFullSheet(): void {
    if (this.fullSheet) {
      this.fullSheet.destroy();
      this.fullSheet = null;
    }
    this.fullSheetOpen = false;
  }

  private close(): void {
    audio.playSfx('select');
    if (this.overlayMode === 'live') {
      const world = this.scene.get('WorldScene') as Phaser.Scene & {
        setModalOverlayOpen?: (open: boolean, source: 'pokedex') => void;
      };
      world.setModalOverlayOpen?.(false, 'pokedex');
      this.scene.stop();
    } else {
      this.scene.stop();
      this.scene.wake('WorldScene');
    }
  }

  private teardown(): void {
    if (this.fullSheet) {
      this.fullSheet.destroy();
      this.fullSheet = null;
    }
    this.tabStrip?.destroy();
    this.tabStrip = null;
    this.modalRoot?.remove();
    this.modalRoot = null;
    this.body = null;
  }
}

function careerEntryToOffer(e: CareerEntry): DraftOffer {
  return {
    id: e.id,
    species: e.species,
    displayName: e.displayName,
    dexId: e.dexId,
    types: [],
    level: e.levelAtDeath ?? e.level,
    moves: [...e.moves],
    movesDisplay: [...e.movesDisplay],
    nature: e.nature,
    ivs: { ...e.ivs },
    ability: e.ability,
    abilityName: e.abilityName,
    // Read-time remap: poke:career entries outlive Reset Run forever —
    // the only way pre-override "No competitive use." text ever dies.
    abilityDesc: gameAbilityDesc(e.ability, e.abilityDesc),
    baseStats: { ...e.baseStats },
    isRarePull: false,
    // Defensive — careerStore's gender + caughtAt are OPTIONAL on the
    // type contract; pre-v9 entries lack them and v9 entries always
    // have them (DraftScene.commit plumbs both). Empty sentinel matches
    // runState's UNKNOWN_CAUGHT_AT shape so render paths stay uniform.
    gender: e.gender ?? 'genderless',
    caughtAt: e.caughtAt
      ? { ...e.caughtAt }
      : { mapName: '', tileX: 0, tileY: 0, when: 0 },
  };
}
